/**
 * Poker Grudge ↔ Grudge Studio fleet SSOT
 * Identity, wallet, GBUX — do not invent parallel hosts.
 */

export const FLEET = {
  publicOrigin: "https://poker.grudge-studio.com",
  grudgeId: "https://id.grudge-studio.com",
  walletSite: "https://wallet.grudge-studio.com",
  gameDataApi: "https://grudge-api-production-0d46.up.railway.app",
  assets: "https://assets.grudge-studio.com",
  open: "https://open.grudge-studio.com",
  /** Studio AI / apps dashboard (player hub) */
  /** Studio dashboard — Grudge ID login + shared account hub */
  puterDashboard: "https://puter.grudge-studio.com/dashboard",
  puterApp: "https://puter.com",
  /**
   * Puter Basic app shell — same SPA as production (index_url → this host).
   * Do not ship a second poker binary on *.puter.site.
   */
  puterPokerApp: "https://puter.com/app/grudge-poker",
  puterPokerAppId: "app-80b4bbba-febe-47a4-b22f-344b5b19be02",
  puterPokerAppName: "grudge-poker",
  /**
   * SNS Web3 profile (gruda.sol → gruda.sol.site).
   * Brand / identity hub — NOT a second player bag DB.
   * Point records at poker or CF Worker for product entry.
   */
  snsHub: "https://gruda.sol.site",
  snsName: "gruda.sol",
  /** Discord community / SSO often via Grudge ID */
  discord: "https://discord.gg/grudge",
  /** Client assistance / cash-out requests (BUDB PKR support) */
  supportEmail: "games@grudgestudio.org",
  supportMailto: "mailto:games@grudgestudio.org",
} as const;

/** Identity providers for account linking — unlock real GBUX access, not free chips */
export const ACCOUNT_LINKS = {
  grudgeId: {
    id: "grudge_id",
    label: "Grudge ID",
    hint: "Email / Discord / social SSO → Crossmint + fleet bag",
    href: "https://id.grudge-studio.com/login",
  },
  puter: {
    id: "puter",
    label: "Puter.js",
    hint: "Puter auth shell → dashboard (not a second bag)",
    href: "https://puter.grudge-studio.com/dashboard",
  },
  email: {
    id: "email",
    label: "Email",
    hint: "Via Grudge ID email login",
    href: "https://id.grudge-studio.com/login",
  },
  discord: {
    id: "discord",
    label: "Discord",
    hint: "Link via Grudge ID Discord provider when enabled",
    href: "https://id.grudge-studio.com/login",
  },
  web3: {
    id: "web3",
    label: "Web3 wallets",
    hint: "Phantom / Solflare / … for on-chain GBUX + deposits",
    href: "/lobby",
  },
  walletSite: {
    id: "crossmint",
    label: "Grudge Wallet (Crossmint)",
    hint: "Custodial Solana wallet for real GBUX",
    href: "https://wallet.grudge-studio.com",
  },
} as const;

/** JWT keys — read all, prefer first present (production-wiring SSOT) */
export const FLEET_AUTH_TOKEN_KEYS = [
  "grudge.open.token",
  "grudge_auth_token",
  "grudge_session_token",
  "grudge.token",
  "sso_token",
  "grudge_token",
] as const;

export const GBUX = {
  mint: "55TpSoMNxbfsNJ9U1dQoo9H3dRtDmjBZVMcKqvU2nray",
  decimals: 9,
  symbol: "GBUX",
  /** USDT mint for USD-stable purchase path (mainnet) */
  usdtMint: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
  /**
   * Product list price — **$0.001 USD = 1 GBUX** (hard product law).
   * SOL purchase: floor(sol × solUsd / rateUsd). USDT: floor(usdt / rateUsd).
   */
  rateUsd: 0.001,
  /** 5% platform fee on SOL/USDT → play GBUX purchases */
  purchaseFeePercent: 0.05,
  /** SOL and USDT both accepted at purchase quote/intent */
  purchaseCurrencies: ["SOL", "USDT"] as const,
  /** Solana production only — never devnet for play money */
  network: "mainnet-beta" as const,
  rpcDefault: "https://api.mainnet-beta.solana.com",
} as const;

/**
 * Crossmint / custodial boundary (production).
 * Player UI: wallet.grudge-studio.com · Server keys: Railway only.
 * Poker SPA: open wallet site + public agent address — no Crossmint client SDK keys required.
 * Server SDK module: server/services/crossmint-wallets.ts (@crossmint/wallets-sdk).
 */
export const CROSSMINT_FLEET = {
  walletSite: "https://wallet.grudge-studio.com",
  /** Production console: crossmint.com/console — JWT auth + production client key live on wallet SPA */
  agentEmail: "poker-ai-agent@grudge-studio.com",
  agentWallet: "6P7Pp5eHzPAVjnbNLkW8DzAuuc7gj9Sm5XiprwnjzvRs",
  /** Wallets SDK V1 chain id — production key ⇒ Solana mainnet */
  chain: "solana" as const,
  restApiVersion: "2025-06-09",
  packages: {
    walletsSdk: "@crossmint/wallets-sdk",
    clientReact: "@crossmint/client-sdk-react-ui",
    /** Primary install on poker for Node server helpers */
    installedOnPoker: "@crossmint/wallets-sdk",
    playerHost: "https://wallet.grudge-studio.com",
  },
  neverOnPokerSpa: [
    "CROSSMINT_SERVER_API_KEY",
    "CROSSMINT_WEBHOOK_SECRET",
    "CROSSMINT_PROJECT_ID",
    "CROSSMINT_SIGNER_SECRET",
  ] as const,
  /** Prefer Railway; optional on Worker only if deliberately set */
  neverOnPokerWorker: [
    "CROSSMINT_SERVER_API_KEY",
    "CROSSMINT_WEBHOOK_SECRET",
    "CROSSMINT_PROJECT_ID",
  ] as const,
} as const;

/** Paths proxied same-origin → Railway (wallet + account + exchange) */
export const FLEET_PROXY_PREFIXES = [
  "/api/wallet",
  "/api/exchange",
  "/api/account",
  "/api/auth",
  "/api/inventory",
] as const;

export type WalletPurchaseCurrency = "SOL" | "USDT";
